'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useReducer, useState } from 'react';
import { reducer } from './reducer';
import { state as initialState } from './state';
import { CLEAR_PENDING_FILES, DELETE_PENDING_FILE, PUSH_FILES, RESET_MEDIA_STATE, SET_DRAG_ACTIVE, SET_ERROR, SET_PROGRESS, SET_UPLOADED_URLS, SET_UPLOADING } from './actions';
import filePrompt from './filePrompt';
import axios from 'axios';
import { noop } from 'lodash';
import { Logger } from './logger';
export const MediaContext = createContext({
    uploadFiles: () => { },
    pushFiles: (files, autoInit) => { },
    setProgress: (progress) => { },
    setUploadedUrls: (uploadedUrls) => { },
    setUploading: (uploading) => { },
    setError: (error) => { },
    reset: () => { },
    setIsDragActive: (isDragActive) => { },
    useMediaState: () => ({}),
    deletePendingFile: (index) => { },
    showPrompt: ({ multiple = false, autoInit = false }) => { }
});
const makeHeaders = (headers) => ({
    headers: {
        ...headers,
        'Content-Type': 'multipart/form-data'
    }
});
export const MediaProvider = ({ children, uploadUrl, headers = {} }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [autoInitUpload, setAutoInitUpload] = useState(false);
    const [onSuccessCallback, setOnSuccessCallback] = useState(noop);
    const controller = (type, payload) => dispatch({
        type,
        payload
    });
    const pushFiles = (files, { autoInit = false, onSuccess } = {}) => {
        autoInit && setAutoInitUpload(autoInit);
        onSuccess && setOnSuccessCallback(onSuccess);
        controller(PUSH_FILES, files);
    };
    const setUploading = (uploading) => controller(SET_UPLOADING, uploading);
    const setProgress = (progress) => controller(SET_PROGRESS, progress);
    const setUploadedUrls = (uploadedUrls) => controller(SET_UPLOADED_URLS, uploadedUrls);
    const setError = (error) => controller(SET_ERROR, error);
    const deletePendingFile = (index) => controller(DELETE_PENDING_FILE, index);
    const setIsDragActive = (isDragActive) => controller(SET_DRAG_ACTIVE, isDragActive);
    const reset = () => controller(RESET_MEDIA_STATE);
    const clearPendingFiles = () => controller(CLEAR_PENDING_FILES);
    const onUploadSuccess = (data) => {
        Logger.log('onUploadSuccess', data);
        setUploadedUrls(data.urls);
        onSuccessCallback(data.urls);
        clearPendingFiles();
        setOnSuccessCallback(noop);
    };
    const handleUpload = async () => {
        Logger.log('handleUpload', state.files);
        if (state.files.length === 0) {
            setError('Please select a file to upload');
            return;
        }
        setUploading(true);
        setError(null);
        const formData = new FormData();
        state.files.forEach((file) => formData.append('files', file));
        try {
            const { data } = await axios.post(uploadUrl, formData, makeHeaders(headers));
            onUploadSuccess(data);
        }
        catch (e) {
            setError(e?.response?.data?.error || 'Upload Failed');
        }
        setUploading(false);
        setAutoInitUpload(false);
    };
    const processFiles = () => {
        setError(null);
        autoInitUpload && handleUpload();
    };
    const showPrompt = async ({ multiple = false, autoInit = false, onSuccess }) => {
        const pendingFiles = await filePrompt({ multiple });
        const acceptedFiles = Object.values(pendingFiles);
        pushFiles(acceptedFiles, { autoInit, onSuccess });
    };
    useEffect(() => {
        state?.files?.length > 0 && processFiles();
    }, [state?.files]);
    const useMediaState = () => {
        return state;
    };
    return (_jsx(MediaContext.Provider, { value: {
            uploadFiles: handleUpload,
            pushFiles,
            setProgress,
            setUploadedUrls,
            setUploading,
            setError,
            reset,
            setIsDragActive,
            deletePendingFile,
            useMediaState,
            showPrompt
        }, children: children }));
};
